import { Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * List-row rename — plan/40.
 * Readout by default; Edit / pencil enters a short Save·Cancel session.
 * No always-on inputs; no blur-save; not double-click-only.
 */

export type ListInlineRenameFieldProps = {
  name: string;
  editing: boolean;
  editValue: string;
  onEditValueChange: (v: string) => void;
  onSave: () => void;
  onCancel: () => void;
  saving?: boolean;
  className?: string;
  /** Optional click on readout (e.g. select price list) */
  onReadoutClick?: () => void;
};

export function ListInlineRenameField({
  name,
  editing,
  editValue,
  onEditValueChange,
  onSave,
  onCancel,
  saving,
  className,
  onReadoutClick,
}: ListInlineRenameFieldProps) {
  if (editing) {
    return (
      <label className="block text-xs text-[var(--color-muted)]">
        Name
        <Input
          className={cn("mt-1", className)}
          value={editValue}
          onChange={(e) => onEditValueChange(e.target.value)}
          autoFocus
          disabled={saving}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onSave();
            }
            if (e.key === "Escape") {
              onCancel();
            }
          }}
        />
      </label>
    );
  }

  if (onReadoutClick) {
    return (
      <button
        type="button"
        className={cn(
          "block max-w-full truncate text-left font-medium text-[var(--color-foreground)]",
          className,
        )}
        onClick={onReadoutClick}
      >
        {name}
      </button>
    );
  }

  return (
    <div className={cn("font-medium", className)}>{name}</div>
  );
}

export type ListInlineRenameActionsProps = {
  editing: boolean;
  editValue: string;
  saving?: boolean;
  startDisabled?: boolean;
  onStartEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  /** Desk: outline “Rename”; compact icon also fine in dense rows */
  variant?: "button" | "icon";
};

export function ListInlineRenameActions({
  editing,
  editValue,
  saving,
  startDisabled,
  onStartEdit,
  onSave,
  onCancel,
  variant = "button",
}: ListInlineRenameActionsProps) {
  if (editing) {
    return (
      <div className="flex flex-wrap justify-end gap-1">
        <Button
          type="button"
          size="sm"
          pending={saving}
          pendingLabel="Saving…"
          disabled={!editValue.trim()}
          onClick={onSave}
        >
          Save
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={saving}
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    );
  }

  if (variant === "icon") {
    return (
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="opacity-70 hover:opacity-100 md:opacity-0 md:group-hover/row:opacity-100 md:focus-visible:opacity-100"
        disabled={startDisabled}
        onClick={onStartEdit}
        aria-label="Rename"
        title="Rename"
      >
        <Pencil className="size-3.5" aria-hidden />
      </Button>
    );
  }

  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      disabled={startDisabled}
      onClick={onStartEdit}
    >
      Rename
    </Button>
  );
}
